import { supabase } from './authService';

/**
 * Fetches all available contract templates
 * @param {boolean} includePrivate - Whether to include private templates owned by the user
 * @returns {Promise<Array>} - Array of template objects
 */
export const getContractTemplates = async (includePrivate = true) => {
  try {
    let query = supabase.from('contract_templates')
      .select('*')
      .order('name');
    
    if (!includePrivate) {
      query = query.eq('is_public', true);
    }
    
    const { data, error } = await query;
    
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching contract templates:', error);
    throw error;
  }
};

/**
 * Fetches a specific contract template by ID
 * @param {string} templateId - The ID of the template to fetch
 * @returns {Promise<Object>} - The template object
 */
export const getContractTemplate = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from('contract_templates')
      .select('*')
      .eq('id', templateId)
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching contract template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Saves a new contract template
 * @param {Object} template - The template object to save
 * @returns {Promise<Object>} - The saved template object
 */
export const saveContractTemplate = async (template) => {
  try {
    const { data, error } = await supabase
      .from('contract_templates')
      .insert([template])
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error saving contract template:', error);
    throw error;
  }
};

/**
 * Updates an existing contract template
 * @param {string} templateId - The ID of the template to update
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object>} - The updated template object
 */
export const updateContractTemplate = async (templateId, updates) => {
  try {
    const { data, error } = await supabase
      .from('contract_templates')
      .update(updates)
      .eq('id', templateId)
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error updating contract template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Deletes a contract template
 * @param {string} templateId - The ID of the template to delete
 * @returns {Promise<void>}
 */
export const deleteContractTemplate = async (templateId) => {
  try {
    const { error } = await supabase
      .from('contract_templates')
      .delete()
      .eq('id', templateId);
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error(`Error deleting contract template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Generates a contract using OpenAI
 * @param {Object|string} optionsOrTemplateType - Either an options object or a template type string
 * @param {Object} [contractDetailsParam] - Contract details (only used if first param is a string)
 * @returns {Promise<string>} - The generated contract text
 */
export const generateContract = async (optionsOrTemplateType, contractDetailsParam) => {
  try {
    let templateType, contractDetails, customPrompt, userDescription;
    
    // Handle both new and old function signatures
    if (typeof optionsOrTemplateType === 'string') {
      // Old signature: generateContract(templateType, contractDetails)
      templateType = optionsOrTemplateType;
      contractDetails = contractDetailsParam;
    } else {
      // New signature: generateContract(options)
      const options = optionsOrTemplateType || {};
      templateType = options.templateType;
      contractDetails = options.contractDetails;
      customPrompt = options.customPrompt;
      userDescription = options.userDescription;
    }
    
    // Validate that we have either template info or a user description
    if (!userDescription && (!templateType || !contractDetails)) {
      throw new Error('Either userDescription or both templateType and contractDetails must be provided');
    }
    
    const response = await fetch('/.netlify/functions/generate-contract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        templateType,
        contractDetails,
        customPrompt,
        userDescription,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to generate contract');
    }
    
    const data = await response.json();
    return data.contract;
  } catch (error) {
    console.error('Error generating contract:', error);
    throw error;
  }
};

/**
 * Generates a contract using a natural language description
 * @param {string} description - User's description of the contract they need
 * @returns {Promise<string>} - The generated contract text
 */
export const generateContractFromDescription = async (description) => {
  return generateContract({ userDescription: description });
};

/**
 * Saves a generated contract
 * @param {Object} contract - The contract object to save
 * @returns {Promise<Object>} - The saved contract object
 */
export const saveGeneratedContract = async (contract) => {
  try {
    const { data, error } = await supabase
      .from('generated_contracts')
      .insert([contract])
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error saving generated contract:', error);
    throw error;
  }
};

/**
 * Fetches all generated contracts for the current user
 * @returns {Promise<Array>} - Array of contract objects
 */
export const getGeneratedContracts = async () => {
  try {
    const { data, error } = await supabase
      .from('generated_contracts')
      .select('*, contract_templates(*)')
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching generated contracts:', error);
    throw error;
  }
};

/**
 * Fetches a specific generated contract by ID
 * @param {string} contractId - The ID of the contract to fetch
 * @returns {Promise<Object>} - The contract object
 */
export const getGeneratedContract = async (contractId) => {
  try {
    const { data, error } = await supabase
      .from('generated_contracts')
      .select('*, contract_templates(*)')
      .eq('id', contractId)
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching generated contract ${contractId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing generated contract
 * @param {string} contractId - The ID of the contract to update
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object>} - The updated contract object
 */
export const updateGeneratedContract = async (contractId, updates) => {
  try {
    const { data, error } = await supabase
      .from('generated_contracts')
      .update(updates)
      .eq('id', contractId)
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error updating generated contract ${contractId}:`, error);
    throw error;
  }
};

/**
 * Deletes a generated contract
 * @param {string} contractId - The ID of the contract to delete
 * @returns {Promise<void>}
 */
export const deleteGeneratedContract = async (contractId) => {
  try {
    const { error } = await supabase
      .from('generated_contracts')
      .delete()
      .eq('id', contractId);
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error(`Error deleting generated contract ${contractId}:`, error);
    throw error;
  }
};

/**
 * Submits a review for a generated contract
 * @param {string} contractId - The ID of the contract to review
 * @param {number} rating - The rating (1-5)
 * @param {string} feedback - Optional feedback text
 * @returns {Promise<Object>} - The saved review object
 */
export const submitContractReview = async (contractId, rating, feedback = '') => {
  try {
    const { data, error } = await supabase
      .from('contract_reviews')
      .insert([{
        contract_id: contractId,
        rating,
        feedback,
      }])
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error submitting review for contract ${contractId}:`, error);
    throw error;
  }
};

/**
 * Fetches reviews for a specific contract template
 * @param {string} templateId - The ID of the template
 * @returns {Promise<Array>} - Array of review objects
 */
export const getTemplateReviews = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from('contract_reviews')
      .select('*, generated_contracts!inner(*)')
      .eq('generated_contracts.template_id', templateId)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error(`Error fetching reviews for template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Converts a generated contract to a document for the e-signature flow
 * @param {string} contractId - The ID of the generated contract
 * @param {string} name - The name for the document
 * @returns {Promise<Object>} - The created document object
 */
export const convertContractToDocument = async (contractId, name) => {
  try {
    // First, get the generated contract
    const contract = await getGeneratedContract(contractId);
    
    if (!contract) {
      throw new Error('Contract not found');
    }
    
    // Create a new document in the documents table
    // Import the storage service
    const storageService = await import('./storageService');
    
    // Convert contract content to PDF (this is a placeholder - you'll need to implement this)
    const pdfData = await convertToPdf(contract.contract_content);
    
    // Import the ID generator
    const idGenerator = await import('./idGenerator');
    
    // Create the document using saveDocument
    const document = await storageService.saveDocument({
      id: idGenerator.generateDocumentId(),
      name: name || `Contract - ${new Date().toLocaleDateString()}`,
      file_data: pdfData,
      file_type: 'application/pdf',
      status: 'draft',
    });
    
    // Update the generated contract with the document ID
    await updateGeneratedContract(contractId, {
      document_id: document.id,
      status: 'converted',
    });
    
    return document;
  } catch (error) {
    console.error(`Error converting contract ${contractId} to document:`, error);
    throw error;
  }
};

/**
 * Placeholder function for converting text to PDF
 * In a real implementation, you would use a library like jsPDF or a service
 * @param {string} text - The text to convert
 * @returns {Promise<string>} - The PDF data
 */
const convertToPdf = async (text) => {
  // This is a placeholder - in a real implementation, you would use a library or service
  // For now, we'll just return the text as a base64 string
  return `data:application/pdf;base64,${btoa(text)}`;
};
